import { useState, useEffect, useRef, useCallback } from 'react'
import { useVotePollMutation, useDeletePollMutation } from '../../store/api/pollsApi'
import type { Poll } from '../../store/api/pollsApi'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { useLoginPrompt } from '../../context/LoginPromptContext'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import Avatar from '../ui/Avatar'

interface Props {
  poll: Poll
  isAuthor: boolean
}

interface Voter { id: string; displayName: string; avatarUrl: string | null }

const VOTERS_PAGE = 20

// ── Voter Popup ───────────────────────────────────────────────
function VoterPopup({
  discussionId,
  optionId,
  optionText,
  onClose,
}: {
  discussionId: string
  optionId: string
  optionText: string
  onClose: () => void
}) {
  const [search, setSearch]       = useState('')
  const [voters, setVoters]       = useState<Voter[]>([])
  const [cursor, setCursor]       = useState<string | null>(null)
  const [hasMore, setHasMore]     = useState(false)
  const [loading, setLoading]     = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef               = useRef<HTMLDivElement>(null)
  const searchTimeout             = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchVoters = useCallback(async (opts: { search: string; cursor?: string | null; append?: boolean }) => {
    const isAppend = opts.append ?? false
    if (isAppend) setLoadingMore(true); else setLoading(true)
    try {
      const params = new URLSearchParams({ optionId, limit: String(VOTERS_PAGE) })
      if (opts.search.trim()) params.set('search', opts.search.trim())
      if (opts.cursor) params.set('cursor', opts.cursor)
      const d = await api.get<{ data: Voter[]; nextCursor: string | null; hasMore: boolean }>(
        'discussions', `${ENDPOINTS.polls.voters(discussionId)}?${params}`
      )
      setVoters(prev => isAppend ? [...prev, ...d.data] : d.data)
      setCursor(d.nextCursor)
      setHasMore(d.hasMore)
    } catch {}
    if (isAppend) setLoadingMore(false); else setLoading(false)
  }, [discussionId, optionId])

  // Initial load
  useEffect(() => {
    void fetchVoters({ search: '' })
  }, [fetchVoters])

  // Debounced search
  const handleSearch = (val: string) => {
    setSearch(val)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      void fetchVoters({ search: val })
    }, 300)
  }

  // Infinite scroll inside popup
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore) return
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
        void fetchVoters({ search, cursor, append: true })
      }
    }, { threshold: 0.1 })
    obs.observe(sentinelRef.current)
    return () => obs.disconnect()
  }, [hasMore, loadingMore, cursor, search, fetchVoters])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', width: '300px',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          zIndex: 201, overflow: 'hidden',
          maxHeight: '70vh',
        }}
      >
        {/* Header */}
        <div style={{ padding: '1rem 1rem 0.75rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                Voted for
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--accent-primary)', marginTop: '0.1rem', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {optionText}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0.1rem', flexShrink: 0 }}
            >✕</button>
          </div>

          {/* Search box */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search voters..."
              autoFocus
              style={{
                width: '100%', padding: '0.45rem 0.75rem 0.45rem 1.75rem',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: '8px', color: 'var(--text-primary)',
                fontSize: '0.8rem', outline: 'none', fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            {search && (
              <button
                onClick={() => handleSearch('')}
                style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1, padding: 0 }}
              >✕</button>
            )}
          </div>
        </div>

        {/* Voter list */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '0.5rem 0' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.5rem 1rem' }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-elevated)', flexShrink: 0 }} />
                  <div style={{ height: 10, width: `${40 + i * 20}%`, background: 'var(--bg-elevated)', borderRadius: '4px' }} />
                </div>
              ))}
            </div>
          )}

          {!loading && voters.length === 0 && (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              {search.trim() ? `No voters matching "${search}"` : 'No votes yet'}
            </div>
          )}

          {!loading && voters.map((voter) => (
            <div
              key={voter.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.45rem 1rem',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
            >
              <Avatar name={voter.displayName} avatarUrl={voter.avatarUrl} size={28} />
              <span style={{ fontSize: '0.825rem', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {voter.displayName}
              </span>
            </div>
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {loadingMore && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Loading more…</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main PollWidget ───────────────────────────────────────────
export default function PollWidget({ poll, isAuthor }: Props) {
  const { isAuthenticated } = useAppSelector((s) => s.auth)
  const { trigger: promptLogin } = useLoginPrompt()
  const [votePoll] = useVotePollMutation()
  const [deletePoll, { isLoading: isDeleting }] = useDeletePollMutation()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [voterPopup, setVoterPopup] = useState<{ optionId: string; text: string } | null>(null)

  const isExpired  = !!poll.expiresAt && new Date() > new Date(poll.expiresAt)
  const hasVoted   = !!poll.userVotedOptionId
  const showBars   = hasVoted || isExpired
  const maxVotes   = Math.max(...poll.options.map(o => o.count), 1)

  const handleVote = async (optionId: string) => {
    if (!isAuthenticated) { promptLogin('Sign in to vote in this poll'); return }
    if (isExpired) return
    await votePoll({ discussionId: poll.discussionId, optionId }).unwrap().catch(() => {})
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await deletePoll(poll.discussionId).unwrap().catch(() => {})
  }

  const expiryLabel = poll.expiresAt
    ? isExpired
      ? 'Poll closed'
      : `Closes ${new Date(poll.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
    : null

  return (
    <>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: '12px', padding: '1.25rem',
        marginBottom: '1rem',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '1rem' }}>📊</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                {poll.question}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                {poll.totalVotes} vote{poll.totalVotes !== 1 ? 's' : ''}
              </span>
              {poll.isAnonymous && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>· Anonymous</span>
              )}
              {!poll.allowRevote && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>· No revoting</span>
              )}
              {expiryLabel && (
                <span style={{
                  fontSize: '0.72rem',
                  color: isExpired ? 'var(--text-muted)' : 'var(--accent-primary)',
                  fontWeight: isExpired ? 400 : 500,
                }}>
                  · {expiryLabel}
                </span>
              )}
            </div>
          </div>

          {isAuthor && !isExpired && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              style={{
                padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid',
                borderColor: confirmDelete ? 'rgba(239,68,68,0.5)' : 'var(--border)',
                background: confirmDelete ? 'rgba(239,68,68,0.08)' : 'transparent',
                color: confirmDelete ? 'var(--error)' : 'var(--text-muted)',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontSize: '0.72rem', fontWeight: 500, flexShrink: 0,
                transition: 'all 0.15s',
              }}
              onMouseLeave={() => setConfirmDelete(false)}
            >
              {confirmDelete ? 'Confirm delete?' : 'Delete poll'}
            </button>
          )}
        </div>

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {poll.options.map((option) => {
            const isVoted   = poll.userVotedOptionId === option.id
            const pct       = poll.totalVotes > 0 ? Math.round((option.count / poll.totalVotes) * 100) : 0
            const isWinning = showBars && option.count === maxVotes && option.count > 0
            const canClick  = !poll.isAnonymous && option.count > 0

            return (
              <div key={option.id}>
                <button
                  type="button"
                  onClick={() => void handleVote(option.id)}
                  disabled={isExpired || (!poll.allowRevote && hasVoted && !isVoted)}
                  style={{
                    width: '100%', position: 'relative', padding: '0',
                    background: 'transparent', border: 'none',
                    cursor: isExpired || (!poll.allowRevote && hasVoted && !isVoted) ? 'default' : 'pointer',
                    borderRadius: '8px', overflow: 'hidden',
                    textAlign: 'left', display: 'block',
                  }}
                >
                  {/* Progress bar fill */}
                  {showBars && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      width: `${pct}%`,
                      background: isVoted
                        ? 'rgba(124,58,237,0.15)'
                        : isWinning
                        ? 'rgba(124,58,237,0.08)'
                        : 'var(--bg-elevated)',
                      borderRadius: '8px',
                      transition: 'width 0.4s ease',
                    }} />
                  )}

                  <div style={{
                    position: 'relative', display: 'flex', alignItems: 'center',
                    gap: '0.5rem', padding: '0.6rem 0.75rem',
                    border: '1px solid',
                    borderColor: isVoted ? 'var(--accent-primary)' : 'var(--border)',
                    borderRadius: '8px', transition: 'border-color 0.15s',
                  }}>
                    {/* Radio dot before voting */}
                    {!showBars && (
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', border: '2px solid',
                        borderColor: isVoted ? 'var(--accent-primary)' : 'var(--border)',
                        background: isVoted ? 'var(--accent-primary)' : 'transparent',
                        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isVoted && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />}
                      </div>
                    )}

                    <span style={{
                      flex: 1, fontSize: '0.875rem',
                      fontWeight: isVoted ? 600 : 400,
                      color: isVoted ? 'var(--accent-primary)' : 'var(--text-primary)',
                    }}>
                      {option.text}
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                      {isWinning && <span style={{ fontSize: '0.7rem' }}>🏆</span>}
                      {showBars && (
                        <span style={{
                          fontSize: '0.8rem', fontWeight: 600,
                          color: isVoted ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          minWidth: '36px', textAlign: 'right',
                        }}>
                          {pct}%
                        </span>
                      )}
                      {/* Count — always visible, clickable if non-anonymous */}
                      <span
                        onClick={canClick ? (e) => {
                          e.stopPropagation()
                          setVoterPopup({ optionId: option.id, text: option.text })
                        } : undefined}
                        style={{
                          fontSize: '0.72rem',
                          color: canClick ? 'var(--accent-primary)' : 'var(--text-muted)',
                          minWidth: '20px', textAlign: 'right',
                          cursor: canClick ? 'pointer' : 'default',
                          fontWeight: canClick ? 600 : 400,
                          textDecoration: canClick ? 'underline' : 'none',
                          textUnderlineOffset: '2px',
                        }}
                      >
                        {option.count}
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            )
          })}
        </div>

        {/* Footer hint */}
        {!isExpired && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {!hasVoted
              ? isAuthenticated
                ? poll.allowRevote ? 'Click to vote · You can change your vote anytime' : 'Click to vote · You cannot change your vote'
                : 'Sign in to vote'
              : poll.allowRevote
              ? 'Click your choice again to unvote, or click another to change your vote'
              : null}
          </div>
        )}
      </div>

      {/* Voter popup — rendered outside the card so z-index is clean */}
      {voterPopup && (
        <VoterPopup
          discussionId={poll.discussionId}
          optionId={voterPopup.optionId}
          optionText={voterPopup.text}
          onClose={() => setVoterPopup(null)}
        />
      )}
    </>
  )
}
